import { AfterViewInit, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, forwardRef, inject, input, InputSignal, Signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseFormField } from '../../form/abstracts';
import { LocationFieldService, GooglePlaceResult } from './services/location-field.service';


@Component({
  selector: 'app-location-field',
  standalone: true,
  imports: [
    TranslateModule, 
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationFieldComponent),
      multi:true,
    },
  ],
  templateUrl: './location-field.component.html',
  styleUrls: ['./location-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationFieldComponent extends BaseFormField<string> implements AfterViewInit {
  // ViewChild references
  public inputLocation: Signal<ElementRef | undefined> = viewChild('inputLocation');
  public switchLocation: Signal<ElementRef | undefined> = viewChild('switchLocation');
  
  // Inputs
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  
  // Services
  private _locationFieldService = inject(LocationFieldService);
  
  // Computed properties
  private _formControl = computed(() => this.form().get(this.formControlName()) as FormControl);
  
  // Validation computed signal
  public isInvalidFieldSignal = computed(() => {
    return this._locationFieldService.isLocationFieldInvalid(this._formControl());
  });

  /**
   * Effect to synchronize location data between form control and service
   * Runs when the start location in the service changes
   */
  syncLocationDataEffect = effect(() => {
    const locationAddress = this._locationFieldService.startLocation()?.address || '';
    
    // Only update if the value has changed to prevent infinite loops
    if (this.value !== locationAddress) {
      this.value = locationAddress;
      
      // Update the FormControl if it exists and value is different
      const control = this._formControl();
      if (control && control.value !== locationAddress) {
        control.setValue(locationAddress, { emitEvent: false });
      }
      
      // Notify change through ControlValueAccessor
      this.onChange(locationAddress);
    }
  }, { allowSignalWrites: true });

  /**
   * Lifecycle hook - Initialize Google Maps Autocomplete after view is ready
   */
  async ngAfterViewInit(): Promise<void> {
    await this._initializeLocationAutocomplete();
  }

  /**
   * Override writeValue to handle synchronization when FormControl changes
   * @param value - The value from the FormControl
   */
  override writeValue(value: string): void {
    super.writeValue(value);

    // Synchronize with service if value differs
    const currentLocation = this._locationFieldService.startLocation();
    if (value && value !== currentLocation?.address) {
      this._locationFieldService.synchronizeLocationData(value, currentLocation);
    }
  }

  /**
   * Handles focus events on the input field
   * @param event - The focus event
   */
  public focusin(event: Event): void {
    event.stopPropagation();
    this.markAsTouched();
    setTimeout(() => {
      this.inputLocation()?.nativeElement.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth'
      });
    }, 350);
  }

  /**
   * Gets the current start location address
   * @returns The current location address or empty string
   */
  public getCurrentStartLocation(): string {
    return this.value || this._locationFieldService.getCurrentStartLocationAddress();
  }

  /**
   * Gets the current end location setting
   * @returns The end location boolean value
   */
  public getCurrentEndLocation(): boolean {
    return this._locationFieldService.endLocation();
  }

  /**
   * Checks if the end location switch should be disabled
   * @returns True if the switch should be disabled
   */
  public isDisabledEndLocationSwitch(): boolean {
    return this._locationFieldService.isEndLocationSwitchDisabled(this.form());
  }

  /**
   * Handles manual input changes in the location field
   * @param event - The input event
   */
  public handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;
    
    // Update BaseFormField state
    this.value = inputValue;
    this.onChange(inputValue);
    this.markAsTouched();
    
    // Update form control
    const control = this._formControl();
    if (control) {
      control.setValue(inputValue, { emitEvent: false });
      control.markAsTouched();
    }
    
    // Clear location data if input is empty
    if (!inputValue || inputValue.trim() === '') {
      this._locationFieldService.clearStartLocation(this.form());
    }
  }

  /**
   * Handles changes to the end location switch
   * @param event - The custom event from the switch component
   */
  public handleSwitchLocationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ checked: boolean }>;
    const isChecked = customEvent.detail.checked;
    
    this._locationFieldService.updateEndLocationSetting(isChecked, this.form());
  }

  // Private methods

  /**
   * Initializes Google Maps Autocomplete for the location input
   * @private
   */
  private async _initializeLocationAutocomplete(): Promise<void> {
    const inputElement = this.inputLocation()?.nativeElement;
    if (!inputElement) {
      console.warn('Location input element not found');
      return;
    }

    try {
      await this._locationFieldService.initializeAutocomplete(
        inputElement,
        (location: GooglePlaceResult) => this._handlePlaceSelection(location)
      );
    } catch (error) {
      console.error('Failed to initialize location autocomplete:', error);
    }
  }

  /**
   * Handles place selection from Google Maps Autocomplete
   * @param location - The selected location data
   * @private
   */
  private _handlePlaceSelection(location: GooglePlaceResult): void {
    // Update service and form through the service
    this._locationFieldService.updateStartLocation(location, this.form());
    
    // Update BaseFormField state
    this.value = location.name;
    this.onChange(this.value);
    this.markAsTouched();
  }
}
